// web/src/stores/artifactsStore.ts
// Manages agent-produced artifacts (persistent documents) and pinned chat responses.
// All state is backend-authoritative — no localStorage.
import { create } from "zustand";
import { api } from "../lib/apiClient";
export const useArtifactsStore = create()((set, get) => ({
    artifacts: [],
    isLoading: false,
    loadArtifacts: async () => {
        if (get().isLoading)
            return;
        set({ isLoading: true });
        try {
            const data = await api.getArtifacts();
            const artifacts = data.artifacts.map((r) => {
                const meta = (r.meta ?? {});
                return {
                    id: r.id,
                    type: r.type,
                    title: r.title,
                    content: r.content,
                    agent_id: r.agent_id,
                    agents_chain: r.agents_chain,
                    conversation_id: r.conversation_id ?? "",
                    reasoning_trace_id: r.reasoning_trace_id ?? undefined,
                    confidence: r.confidence ?? undefined,
                    created_at: r.created_at,
                    renders: meta.renders ?? undefined,
                    message_id: meta.message_id ?? undefined,
                };
            });
            set({ artifacts });
        }
        catch {
            // Non-fatal — degraded gracefully with empty list
        }
        finally {
            set({ isLoading: false });
        }
    },
    addArtifact: async (artifact) => {
        // Optimistically add with a temp ID so isPinned() is true immediately.
        const tempId = `temp-${Date.now()}`;
        const optimistic = { ...artifact, id: tempId, created_at: new Date().toISOString() };
        set((s) => ({ artifacts: [optimistic, ...s.artifacts] }));
        try {
            const meta = {};
            if (artifact.message_id)
                meta.message_id = artifact.message_id;
            if (artifact.renders)
                meta.renders = artifact.renders;
            const created = await api.createArtifact({
                type: artifact.type,
                title: artifact.title,
                content: artifact.content,
                agent_id: artifact.agent_id,
                agents_chain: artifact.agents_chain,
                conversation_id: artifact.conversation_id || null,
                reasoning_trace_id: artifact.reasoning_trace_id || null,
                confidence: artifact.confidence ?? null,
                meta: Object.keys(meta).length > 0 ? meta : null,
            });
            // Replace the temp entry with the server-assigned ID.
            set((s) => ({
                artifacts: s.artifacts.map((a) => a.id === tempId ? { ...optimistic, id: created.id, created_at: created.created_at } : a),
            }));
        }
        catch {
            // Roll back the optimistic entry on failure.
            set((s) => ({ artifacts: s.artifacts.filter((a) => a.id !== tempId) }));
        }
    },
    deleteArtifact: async (id) => {
        // Optimistically remove from state, then confirm deletion on backend.
        set((s) => ({ artifacts: s.artifacts.filter((a) => a.id !== id) }));
        try {
            await api.deleteArtifact(id);
        }
        catch {
            // If deletion fails, reload from backend to restore correct state.
            void get().loadArtifacts();
        }
    },
    getArtifact: (id) => get().artifacts.find((a) => a.id === id),
    isPinned: (messageId) => get().artifacts.some((a) => a.type === "pinned_message" && a.message_id === messageId),
}));
